import { Box, IconButton, Typography } from '@mui/material'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { IconArrowLeft } from '@tabler/icons-react'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { PredictedDish } from '../../../types/menuTypes'
import MenuScanDishList from './components/MenuScanDishList'
import MenuScanAISummary from './components/MenuScanAISummary'
import { ROUTES } from '../../../helpers/routes-helper'
import { setPendingSaveMenuIntent } from '../../../helpers/menuScanIntent'
import { useAuthHelper } from '../../../hooks/useAuthHelper'
import { useDevice } from '../../../hooks/useDevice'
import { useMenuScanFlow } from '../../../hooks/useMenuScanFlow'
import { MuiAlert, MuiSnackbar } from '../styled/global.styled'
import {
  ButtonsContainer,
  CategoryChip,
  CategoryChipsContainer,
  HeadingContainer,
  HeadingNumber,
  HeadingText,
  SaveMenuButton,
  ScanAnotherMenuButton,
} from './styled/MenuScanResults.styled'
import { infoLog, errorLog } from '../../../utils/log-helper'
import { logEvent } from '../../../analytics/analytics'
import {
  emitCameraPromptIfNeeded,
  emitCameraDeniedIfDenied,
} from '../../../helpers/camera-analytics'
import MenuScanCoachingSheet, {
  useMenuScanCoaching,
} from '../../../components/MenuScanCoachingSheet'
import { Helmet } from 'react-helmet'
import ProfileCompletionBanner from '../../../components/ProfileCompletionBanner'
import {
  MENU_SCAN_STATUS,
  type MenuScanResult,
  type MenuScanDish,
} from '../../../api/menuScanApi'
import { useAppDispatch, useAppSelector } from '../../../store'
import {
  getMenuScanStatusThunk,
  getMenuScanSummaryThunk,
  saveMenuFromScanThunk,
  selectMenuScanStatus,
  selectMenuScanSummary,
  selectMenuScanSave,
} from '../../../store/menuScanSlice'

function mapApiDishesToPredicted(dishes: MenuScanDish[]): PredictedDish[] {
  return dishes.map((d, i) => {
    const price =
      typeof d.price === 'number'
        ? d.price
        : typeof d.price === 'string' && d.price !== ''
          ? parseFloat(d.price) || 0
          : 0
    return {
      dish: {
        dishID: `scan-${i}`,
        name: d.name ?? '',
        price,
        ingredients: '',
        description: d.description ?? '',
        orderCount: 0,
        tags: '',
        status: 'COMPLETED',
        created: '',
        updated: '',
        dietary: '',
        unsuitabledietary: '',
        preparation: '',
        imageUrl: '',
        starCount: 0,
        reviews: [],
      },
      probability: {
        yes: 0,
        no: 0,
        matchLabel: d.section ?? 'Main',
      },
    }
  })
}

const MenuScanResults: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuth0()
  const { isHybrid } = useDevice()
  const { loginAuthUser } = useAuthHelper()
  const { startScan, uploading } = useMenuScanFlow()
  const dispatch = useAppDispatch()
  const { loading: statusLoading, error: fetchError } = useAppSelector(selectMenuScanStatus)
  const { data: summary, loading: summaryLoading, error: summaryError } = useAppSelector(selectMenuScanSummary)
  const { saving, error: saveError } = useAppSelector(selectMenuScanSave)
  
  const state = location.state as
    | { scanId?: string; result?: MenuScanResult; saveError?: string }
    | undefined
  const [result, setResult] = useState<MenuScanResult | null>(state?.result ?? null)
  const [localSaveError, setLocalSaveError] = useState<string | null>(state?.saveError ?? null)
  const summaryRequestedForRef = useRef<string | null>(null)
  const resultsViewedForRef = useRef<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { requestCapture, coachingProps } = useMenuScanCoaching(
    'menu_scan_results',
    'results_retry',
  )

  useEffect(() => {
    if (state?.result) {
      setResult(state.result)
      return
    }
    if (!state?.scanId) {
      infoLog('[MenuScanResults] No scanId or result in state')
      return
    }
    const fetchResult = async (): Promise<void> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const data = await dispatch(getMenuScanStatusThunk(state.scanId!)).unwrap()
        setResult(data)
      } catch (e) {
        errorLog('[MenuScanResults] Failed to fetch result', e)
      }
    }
    void fetchResult()
  }, [state?.scanId, state?.result, dispatch])

  // Fire once per scan result when results are rendered
  useEffect(() => {
    if (!result?.scanId || resultsViewedForRef.current === result.scanId) return
    resultsViewedForRef.current = result.scanId
    logEvent('menu_results_viewed', {
      result_count: result.dishes?.length ?? 0,
      has_restaurant_context: !!(result.restaurantId),
      // restaurant_id is dropped by sanitizeParams when null/undefined — only sent when the
      // scan resolved to a restaurant, so rank attribution stays clean.
      restaurant_id: result.restaurantId ?? undefined,
      screen_name: 'menu_scan_results',
    })
  }, [result?.scanId, result?.dishes?.length, result?.restaurantId])

  useEffect(() => {
    if (
      !result?.scanId ||
      !result.dishes?.length ||
      summaryRequestedForRef.current === result.scanId
    ) return
    summaryRequestedForRef.current = result.scanId
    const payload = {
      scanId: result.scanId,
      restaurantName: null,
      dishes: result.dishes.map((d) => ({
        name: d.name ?? '',
        description: d.description ?? null,
        price: typeof d.price === 'number' ? d.price : typeof d.price === 'string' && d.price !== '' ? parseFloat(d.price) || null : null,
        section: d.section ?? null,
      })),
      userContext: null,
    }
    void dispatch(getMenuScanSummaryThunk(payload))
  }, [result?.scanId, result?.dishes, dispatch])

  const isFailed = result?.status === MENU_SCAN_STATUS.FAILED
  const hasRestaurantContext = !!(result?.restaurantId)
  const dishes: PredictedDish[] = result?.dishes?.length
    ? mapApiDishesToPredicted(result.dishes)
    : []

  const categories = Array.from(
    new Set(dishes.map((d) => d.probability?.matchLabel ?? 'Main').filter(Boolean))
  ).slice(0, 6)
  if (categories.length === 0) categories.push('Dishes')

  const summaryFallback = !!summaryError
  const displaySaveError = saveError ?? localSaveError

  const handleSaveMenu = (): void => {
    const scanId = result?.scanId
    if (!scanId) {
      setLocalSaveError('No scan to save.')
      return
    }
    if (!isAuthenticated) {
      setPendingSaveMenuIntent({
        scanId,
        dishes: result?.dishes ?? [],
        restaurantId: result?.restaurantId ?? undefined,
        returnRoute: ROUTES.MENU_SCAN_RESULTS,
      })
      // Mark signup source so onboarding completion can attribute it to the scan funnel
      sessionStorage.setItem('signup_source', 'menu_scan')
      logEvent('signup_started', { screen_name: 'menu_scan_results', source: 'menu_scan' })
      loginAuthUser()
      return
    }
    setLocalSaveError(null)
    dispatch(saveMenuFromScanThunk({
      scanId,
      request: {
        restaurantId: result?.restaurantId ?? undefined,
        menuName: 'Scanned Menu',
        dishes: result?.dishes ?? [],
      }
    }))
      .unwrap()
      .then((response) => {
        navigate(ROUTES.MENU_SAVE_SUCCESS, { 
          state: { 
            saveResponse: response,
            restaurantId: result?.restaurantId,
          } 
        })
      })
      .catch((e) => {
        errorLog('[MenuScanResults] Save menu failed', e)
        setLocalSaveError('Could not save menu. Please try again.')
      })
  }

  const handleScanAnotherMenu = (): void => {
    sessionStorage.removeItem('menuScanFile')
    sessionStorage.removeItem('menuScanFileName')
    sessionStorage.removeItem('menuScanFileType')
    sessionStorage.removeItem('menuScanRestaurantAlias')
    infoLog('[MenuScanResults] Try another menu')
    logEvent('menu_scan_opened', { screen_name: 'menu_scan_results', entry_point: 'results_retry' })
    // Show the pre-capture coaching cue first; the existing capture path runs on confirm.
    requestCapture(() => {
      if (isHybrid || Capacitor.isNativePlatform()) {
        void (async (): Promise<void> => {
          try {
            await emitCameraPromptIfNeeded('menu_scan_results')
            logEvent('menu_scan_capture_shown', { screen_name: 'menu_scan_results' })
            const photo = await Camera.getPhoto({
              quality: 90,
              resultType: CameraResultType.DataUrl,
              source: CameraSource.Camera,
              allowEditing: false,
              promptLabelHeader: 'Scan a menu',
              promptLabelPhoto: 'Choose menu photo',
              promptLabelPicture: 'Take menu photo',
            })
            logEvent('camera_permission', { status: 'granted', screen_name: 'menu_scan_results' })
            if (!photo.dataUrl) return
            const res = await fetch(photo.dataUrl)
            const blob = await res.blob()
            const file = new File(
              [blob],
              `menu-${Date.now()}.jpg`,
              { type: blob.type || 'image/jpeg' },
            )
            void startScan(file, result?.restaurantId ?? undefined).catch(() => {})
          } catch (e) {
            void emitCameraDeniedIfDenied('menu_scan_results')
            errorLog('[MenuScanResults] Camera error', e)
          }
        })()
      } else {
        logEvent('menu_scan_capture_shown', { screen_name: 'menu_scan_results' })
        fileInputRef.current?.click()
      }
    })
  }

  const handleScanAnotherFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event?.target?.files?.[0]
    event.target.value = ''
    if (!file) return
    void startScan(file, result?.restaurantId ?? undefined).catch(() => {})
  }

  if (statusLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading results…</Typography>
      </Box>
    )
  }

  if (fetchError ?? (isFailed && !result?.dishes?.length)) {
    // A failed scan is almost always "the photo wasn't a readable menu" (users point the
    // camera at anything to try the feature) — coach them instead of echoing the raw
    // backend error. Genuine fetch errors keep the raw message below as a fallback.
    return (
      <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
        {isFailed ? (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, mb: 1 }}>
              We couldn&apos;t spot a menu in that photo
            </Typography>
            <Typography sx={{ color: '#6B7580', mb: 1 }}>
              For the best results, take a photo of a printed menu:
            </Typography>
            <Typography component="ul" sx={{ color: '#6B7580', mb: 2, pl: 3, '& li': { mb: 0.5 } }}>
              <li>Hold the camera straight on, so the menu fills the frame</li>
              <li>Make sure the text is in focus and well lit</li>
              <li>One page at a time works better than the whole table</li>
            </Typography>
          </>
        ) : (
          <Typography color="error" sx={{ mb: 2 }}>
            {fetchError ?? 'Analysis failed.'}
          </Typography>
        )}
        <ScanAnotherMenuButton onClick={handleScanAnotherMenu}>
          {isFailed ? 'Take another photo' : 'Try another menu'}
        </ScanAnotherMenuButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleScanAnotherFileChange}
          disabled={uploading}
        />
        <MenuScanCoachingSheet {...coachingProps} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: { xs: '16px', sm: '20px', md: '24px' },
        maxWidth: '1000px',
        margin: '0 auto',
        overflowX: 'hidden',
      }}
    >
      <Helmet>
        <title>Resactly | Menu Scan Results</title>
      </Helmet>

      {/* Lightweight header — only on hybrid where top NavBar is hidden */}
      {isHybrid && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mb: '16px',
            pb: '12px',
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="go back"
            sx={{ padding: '8px', marginLeft: '-8px' }}
          >
            <IconArrowLeft size={24} stroke={2} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' } }}
          >
            Your Menu
          </Typography>
        </Box>
      )}

      <HeadingContainer>
        {dishes.length === 0 ? (
          <>
            <HeadingText>We couldn&apos;t find a menu</HeadingText>
            <Typography
              sx={{
                fontSize: { xs: '15px', sm: '16px' },
                color: '#6B7580',
                mt: '8px',
                fontWeight: 400,
              }}
            >
              Point your camera straight at a menu so the dish names and prices
              are clearly readable — avoid glare, shadows, and blur. Photos of
              food, people, or anything that isn&apos;t a menu won&apos;t work.
            </Typography>
          </>
        ) : (
          <HeadingText>
            <HeadingNumber>{dishes.length}</HeadingNumber> dishes worth considering
          </HeadingText>
        )}
      </HeadingContainer>

      {dishes.length > 0 && (
        <>
          <CategoryChipsContainer>
            {categories.map((category, index) => (
              <CategoryChip key={index} label={category} variant="outlined" />
            ))}
          </CategoryChipsContainer>

          <Box sx={{ mt: 2, mb: 2 }}>
            <ProfileCompletionBanner />
          </Box>

          {!summaryFallback && (
            <MenuScanAISummary
              quickTake={summary?.quickTake ?? ''}
              goodFor={summary?.goodFor ?? ''}
              watchOut={summary?.watchOut ?? ''}
              friendPick={summary?.friendPick ?? ''}
              isLoading={summaryLoading}
              isFailed={summaryFallback}
            />
          )}

          <MenuScanDishList dishes={dishes} scanId={result?.scanId ?? state?.scanId} hasRestaurantContext={hasRestaurantContext} restaurantId={result?.restaurantId ?? undefined} />
        </>
      )}

      <ButtonsContainer>
        <ScanAnotherMenuButton onClick={handleScanAnotherMenu} disabled={uploading}>
          {dishes.length === 0
            ? uploading
              ? 'Scanning…'
              : 'Scan a menu'
            : 'Try another menu'}
        </ScanAnotherMenuButton>
        {dishes.length > 0 && (
          <SaveMenuButton onClick={handleSaveMenu} disabled={saving}>
            {saving ? 'Saving…' : 'Save menu'}
          </SaveMenuButton>
        )}
      </ButtonsContainer>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleScanAnotherFileChange}
        disabled={uploading}
      />
      <MuiSnackbar
        open={displaySaveError != null}
        autoHideDuration={5000}
        onClose={() => setLocalSaveError(null)}
      >
        <MuiAlert severity="error" onClose={() => setLocalSaveError(null)}>
          {displaySaveError}
        </MuiAlert>
      </MuiSnackbar>
      <MenuScanCoachingSheet {...coachingProps} />
    </Box>
  )
}

export default MenuScanResults
